import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { nowISO } from './../utils/dateUtils';
import { logger } from "../utils/logger";

const MAX_TABS = 10;

/**
 * Tab System Hook
 * 
 * Manages Chrome-style tabs with localStorage persistence
 */
export function useTabSystem(userId) {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [hasRestoredSession, setHasRestoredSession] = useState(false);
  const tabsRef = useRef(tabs);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards the save effect so it cannot fire before the initial restore.
  // Without this, a full-page reload (e.g. after OAuth, post-claim auth refresh,
  // or any `window.location.href` hop to an in-app route) would start with
  // `tabs = []`, Layout's path-watch effect would immediately add one tab for
  // the current route, and the save effect would overwrite the saved session
  // with just that single tab — silently wiping every other tab the user had open.
  const didRestoreRef = useRef(false);

  const storageKey = `kindredly_tabs_${userId}`;

  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  useEffect(() => {
    if (!userId) return undefined;

    let restoredTabs: unknown[] = [];
    let restoredActive: string | null = null;
    try {
      const savedSession = localStorage.getItem(storageKey);
      if (savedSession) {
        const parsed = JSON.parse(savedSession);
        restoredTabs = Array.isArray(parsed?.tabs) ? parsed.tabs : [];
        restoredActive = parsed?.activeTabId ?? null;
      }
    } catch (error) {
      logger.error('Error loading tab session:', error);
    }

    if (restoredTabs.length > 0) {
      // Merge with any tabs Layout may have already opened for the current
      // route (effect ordering: child effects run before parent, so Layout's
      // openTab may have fired before this restore effect).  Dedup by route.
      const restored = restoredTabs as Array<{ route: string }>;
      setTabs((current: Array<{ route: string }>) => {
        let result;
        if (current.length === 0) {
          result = restored;
        } else {
          const restoredRoutes = new Set(restored.map(t => t.route));
          const newItems = current.filter(t => !restoredRoutes.has(t.route));
          result = [...restored, ...newItems];
        }
        tabsRef.current = result;
        return result;
      });
      setActiveTabId(current => current || restoredActive);
    }

    setHasRestoredSession(true);
    didRestoreRef.current = true;

    return () => {
      // When userId changes (logout / user switch), re-gate the save effect
      // so the next user's tabs can't get written to the previous user's key.
      didRestoreRef.current = false;
    };
  }, [userId, storageKey]);

  useEffect(() => {
    if (!userId || tabs.length === 0) return;
    // Skip saves until the initial restore has completed — otherwise the
    // first `openTab` from Layout on a full-page reload would persist a
    // single-tab session over the real saved state.
    if (!didRestoreRef.current) return;

    // Debounce localStorage writes so rapid tab/route changes don't
    // block the main thread with JSON.stringify on every frame.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        const session = {
          userId,
          activeTabId,
          tabs,
          lastSaved: nowISO()
        };
        localStorage.setItem(storageKey, JSON.stringify(session));
      } catch (error) {
        logger.error('Error saving tab session:', error);
      }
    }, 300);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        // Flush the pending save so tab state isn't lost on unmount/user switch
        try {
          const session = { userId, activeTabId, tabs, lastSaved: nowISO() };
          localStorage.setItem(storageKey, JSON.stringify(session));
        } catch { /* ignore */ }
      }
    };
  }, [tabs, activeTabId, userId, storageKey]);
  
  const getSavedTabs = useCallback(() => {
    if (!userId) return null;
    
    try {
      const savedSession = localStorage.getItem(storageKey);
      if (savedSession) {
        return JSON.parse(savedSession);
      }
    } catch (error) {
      logger.error('Error getting saved tabs:', error);
    }
    return null;
  }, [userId, storageKey]);
  
  const restoreSavedTabs = useCallback(() => {
    const saved = getSavedTabs();
    if (saved && saved.tabs) {
      tabsRef.current = saved.tabs;
      setTabs(saved.tabs);
      setActiveTabId(saved.activeTabId || saved.tabs[0]?.id);
      setHasRestoredSession(true);
    }
  }, [getSavedTabs]);
  
  const clearSavedTabs = useCallback((targetUserId = userId) => {
    if (!targetUserId) return;
    
    try {
      localStorage.removeItem(`kindredly_tabs_${targetUserId}`);
      if (targetUserId !== userId) return;
      tabsRef.current = [];
      setTabs([]);
      setActiveTabId(null);
      setHasRestoredSession(true);
    } catch (error) {
      logger.error('Error clearing tabs:', error);
    }
  }, [userId]);
  
  // Extract the pathname portion from a route that may include query params
  const getPathname = useCallback((route: string) => {
    const qIndex = route.indexOf('?');
    return qIndex >= 0 ? route.slice(0, qIndex) : route;
  }, []);

  const isDashboardRoute = useCallback((path) => {
    const p = getPathname(path);
    return p === '/dashboard' || p.match(/^\/[^/]+\/dashboard$/);
  }, [getPathname]);

  // Map a path to a "section key".  Two paths that return the same key
  // belong to the same logical section and should share a single tab.
  //
  //   /clients/abc/info   → "/clients/abc"   (entity-scoped)
  //   /notes/new           → "/notes"          (section-scoped)
  //   /billing/invoices    → "/billing"        (billing-domain)
  //   /invoices            → "/billing"        (billing-domain, old route)
  //   /admin/leads/abc     → "/admin/leads"    (admin-scoped)
  const getSectionKey = useCallback((path: string): string | null => {
    const clean = getPathname(path);

    // Entity-scoped groups — each client / case gets its own tab
    const entityMatch = clean.match(/^\/(clients|cases)\/[^/]+/);
    if (entityMatch) return entityMatch[0];

    const segments = clean.split('/').filter(Boolean);
    const first = segments[0];

    // Billing domain — all billing-related routes share one "Billing" tab.
    // This covers both new routes (/billing/invoices) and old redirecting
    // routes (/invoices, /payments, /superbills, /insurance).
    const billingPaths = new Set([
      'billing', 'invoices', 'payments', 'superbills',
      'insurance', 'payer-payments', 'gfe', 'ar',
    ]);
    if (first && billingPaths.has(first)) return '/billing';

    // Analytics domain — all analytics/reports routes share one "Analytics" tab.
    // This covers the canonical /analytics route and legacy admin routes
    // that redirect there (/admin/kpi, /admin/reports, /admin/analytics,
    // /admin/provider-analytics, /admin/revenue).
    if (first === 'admin') {
      const second = segments[1];
      const analyticsAdminPaths = new Set(['kpi', 'reports', 'analytics', 'provider-analytics', 'revenue']);
      if (second && analyticsAdminPaths.has(second)) return '/analytics';
    }

    // Section groups — all sub-routes share one tab
    const sectionGroups = new Set([
      'notes',
      'messages', 'tasks', 'todos', 'todo',
      'projects', 'library', 'work-products', 'vault', 'supervision', 'templates',
      // Scheduling & misc provider sections
      'calendar', 'leads', 'analytics',
      'automation', 'reflections', 'outcomes', 'notifications',
      'drive-mode', 'my-settings', 'my-work',
    ]);
    if (first && sectionGroups.has(first)) return `/${first}`;

    // Admin-scoped groups — each admin section gets its own tab
    // e.g. /admin/settings?tab=billing → "/admin/settings"
    //      /admin/leads/abc123        → "/admin/leads"
    if (first === 'admin') {
      const second = segments[1];
      return second ? `/${first}/${second}` : '/admin';
    }

    return null;
  }, [getPathname]);

  const getEntityKey = useCallback((path: string): string | null => {
    const clean = getPathname(path);
    const match = clean.match(/^\/(clients|cases)\/[^/]+/);
    return match ? match[0] : null;
  }, [getPathname]);

  const openTab = useCallback((route, title, icon = null) => {
    let tabId = null;
    
    setTabs(prev => {
      // Match by exact route first, then fall back to pathname match so that
      // navigating to /ai-assistant?mode=guided_today reuses the /ai-assistant tab
      // instead of creating a duplicate.
      const routePathname = getPathname(route);
      const existingTab = prev.find(t => t.route === route) ||
                          prev.find(t => getPathname(t.route) === routePathname);
      if (existingTab) {
        tabId = existingTab.id;
        // Update the stored route to include the latest query params
        if (existingTab.route !== route) {
          const result = prev.map(t => t.id === existingTab.id ? { ...t, route, lastAccessed: nowISO() } : t);
          tabsRef.current = result;
          return result;
        }
        return prev;
      }

      // Section-key dedup: when a redirect fires (e.g. /admin/kpi → /analytics),
      // the Layout effect may call openTab twice in rapid succession — once for
      // the legacy route and once for the canonical route.  Both map to the same
      // section key (e.g. "/analytics").  Updating tabsRef synchronously ensures
      // findParentTab (which reads tabsRef.current) sees tabs created by the
      // first call even before React re-renders, preventing a duplicate tab.
      const key = getSectionKey(routePathname);
      if (key) {
        const sectionTab = prev.find(t => getSectionKey(getPathname(t.route)) === key);
        if (sectionTab) {
          tabId = sectionTab.id;
          const result = prev.map(t => t.id === sectionTab.id ? { ...t, route, title, lastAccessed: nowISO() } : t);
          tabsRef.current = result;
          return result;
        }
      }
      
      if (isDashboardRoute(route)) {
        const dashTab = prev.find(t => isDashboardRoute(t.route));
        if (dashTab) {
          tabId = dashTab.id;
          const result = prev.map(t => t.id === dashTab.id ? { ...t, route, title, lastAccessed: nowISO() } : t);
          tabsRef.current = result;
          return result;
        }
      }
      
      if (prev.length >= MAX_TABS) {
        return prev;
      }
      
      const newTab = {
        id: uuidv4(),
        title,
        route,
        icon,
        pinned: false,
        position: prev.length,
        lastAccessed: nowISO(),
        createdAt: nowISO()
      };
      
      tabId = newTab.id;
      const result = [...prev, newTab];
      tabsRef.current = result;
      return result;
    });
    
    if (tabId) {
      setActiveTabId(tabId);
    }
    
    return tabId;
  }, [isDashboardRoute, getPathname, getSectionKey]);
  
  const closeTab = useCallback((tabId) => {
    let nextRoute = null;

    setTabs(prev => {
      const closingIndex = prev.findIndex(t => t.id === tabId);
      if (closingIndex === -1) return prev;

      const newTabs = prev.filter(t => t.id !== tabId);

      setActiveTabId(currentActiveId => {
        if (tabId === currentActiveId) {
          if (newTabs.length > 0) {
            const nextIndex = Math.min(closingIndex, newTabs.length - 1);
            nextRoute = newTabs[nextIndex].route;
            return newTabs[nextIndex].id;
          } else {
            nextRoute = '/dashboard';
            return null;
          }
        }
        return currentActiveId;
      });

      tabsRef.current = newTabs;
      return newTabs;
    });

    return nextRoute;
  }, []);
  
  const switchTab = useCallback((tabId) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === tabId);
      if (!tab) return prev;
      
      setActiveTabId(tabId);
      
      return prev.map(t => 
        t.id === tabId 
          ? { ...t, lastAccessed: nowISO() }
          : t
      );
    });
  }, []);
  
  const reorderTabs = useCallback((tabId, newPosition) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === tabId);
      if (!tab) return prev;
      
      const reordered = [...prev];
      const oldPosition = prev.findIndex(t => t.id === tabId);
      
      reordered.splice(oldPosition, 1);
      reordered.splice(newPosition, 0, tab);
      
      const result = reordered.map((t, index) => ({
        ...t,
        position: index
      }));
      tabsRef.current = result;
      return result;
    });
  }, []);
  
  const togglePinTab = useCallback((tabId) => {
    setTabs(prev => {
      const result = prev.map(t => 
        t.id === tabId 
          ? { ...t, pinned: !t.pinned }
          : t
      );
      tabsRef.current = result;
      return result;
    });
  }, []);
  
  // Update an existing tab's route without creating a new tab
  // Used for sub-tab navigation within the same parent context (e.g., client chart sub-tabs)
  // `newDisambiguator` (e.g. a DOB cue) is shown only when another open tab has
  // the same title; `undefined` keeps the current value for the same entity.
  const updateTabRoute = useCallback((tabId, newRoute, newTitle = null, newDisambiguator: string | null | undefined = undefined) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === tabId);
      if (!tab) return prev;

      const nextTitle = newTitle || tab.title;
      const sameEntity = getEntityKey(tab.route) === getEntityKey(newRoute);
      const nextDisambiguator = newDisambiguator !== undefined
        ? newDisambiguator
        : (sameEntity ? tab.disambiguator ?? null : null);
      if (
        tab.route === newRoute
        && tab.title === nextTitle
        && (tab.disambiguator ?? null) === nextDisambiguator
      ) return prev;

      const result = prev.map(t =>
        t.id === tabId
          ? { ...t, route: newRoute, title: nextTitle, disambiguator: nextDisambiguator, lastAccessed: nowISO() }
          : t
      );
      tabsRef.current = result;
      return result;
    });
  }, [getEntityKey]);

  const findParentTab = useCallback((path) => {
    const currentTabs = tabsRef.current;

    // Entity-scoped routes share one tab per client/case, including the
    // entity index route before it redirects to its default section.
    const entityKey = getEntityKey(path);
    if (entityKey) {
      const parentTab = currentTabs.find(t => getEntityKey(t.route) === entityKey);
      if (parentTab) return parentTab;
    }

    // Section-group matching — find an existing tab in the same section
    const key = getSectionKey(path);
    if (key) {
      return currentTabs.find(t => getSectionKey(t.route) === key);
    }

    return null;
  }, [getEntityKey, getSectionKey]);
  
  const activeTab = tabs.find(t => t.id === activeTabId);
  
  return {
    tabs,
    activeTab,
    activeTabId,
    hasRestoredSession,
    getSavedTabs,
    restoreSavedTabs,
    clearSavedTabs,
    openTab,
    closeTab,
    switchTab,
    reorderTabs,
    togglePinTab,
    updateTabRoute,
    findParentTab,
    maxTabs: MAX_TABS
  };
}

export default useTabSystem;
